import React, { useMemo } from "react";

const money = (amount) =>
  `KSh ${Number(amount || 0).toLocaleString("en-KE", { maximumFractionDigits: 2 })}`;

const normalize = (v) => String(v || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
const statusOf = (job) => normalize(job?.status);
const dispatchOf = (job) => normalize(job?.dispatch_status);
const serviceOf = (job) => job?.service || job?.service_name || job?.title || "Service request";
const craftsmanOf = (job) => (job?.craftsman && typeof job.craftsman === "object" ? job.craftsman : null);
const craftsmanName = (job) =>
  craftsmanOf(job)?.full_name || craftsmanOf(job)?.name || job?.craftsman_name || null;
const jobKey = (job, i = 0) => job?.id ?? job?.request_id ?? `${serviceOf(job)}-${job?.created_at || i}`;

const isPaid = (job) =>
  /paid|payment_complete|payment_completed/.test(statusOf(job)) ||
  /^(paid|successful)$/.test(normalize(job?.payment_status));
const isCancelled = (job) => /cancel|declin|reject/.test(`${statusOf(job)} ${dispatchOf(job)}`);
const isCompleted = (job) =>
  /complet|closed/.test(statusOf(job)) || /complet|closed/.test(dispatchOf(job)) || isPaid(job);
const isTrackable = (job) => !isPaid(job) && !isCancelled(job) && !isCompleted(job);
const paidAmount = (job) =>
  Number(job?.amount_paid ?? job?.paid_amount ?? job?.total_paid ?? job?.budget ?? 0) || 0;

const STEPS = ["Matching", "Assigned", "On the way", "Arrived", "Working", "Done"];
const HEADLINES = [
  "Finding your craftsman",
  "Your craftsman has been assigned",
  "Your craftsman is on the way",
  "Your craftsman has arrived",
  "Work is in progress",
  "Your job is complete",
];

const getStep = (job) => {
  const s = `${statusOf(job)} ${dispatchOf(job)}`;
  if (/complet|closed|paid/.test(s)) return 5;
  if (/in_progress|working|started/.test(s)) return 4;
  if (/arrived|at_location/.test(s)) return 3;
  if (/on_way|on_the_way|en_route|travelling|traveling/.test(s)) return 2;
  if (/assign|accept|approv|confirm|quote_submitted/.test(s)) return 1;
  return 0;
};

const getJobTime = (job) => {
  const v = job?.paid_at || job?.updated_at || job?.created_at || job?.schedule;
  if (!v) return 0;
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : 0;
};

const avatar = (name) =>
  `https://ui-avatars.com/api/?name=${encodeURIComponent(name || "Craftsman")}&background=0c0c0b&color=fff000&size=128&bold=true`;

const CSS = `
.cd { --ink:#0c0c0b; --muted:#6b6b66; --line:#e6e6e1; --soft:#f3f3ef; --y:#fff000; --good:#1f7a3d; --bad:#b3261e;
  max-width: 1160px; margin: 0 auto; color: var(--ink); font-family: inherit; font-size: 14px; line-height: 1.5; }
.cd *, .cd *::before, .cd *::after { box-sizing: border-box; }
.cd h1, .cd h2, .cd h3, .cd p { margin: 0; }
.cd button { font: inherit; cursor: pointer; }
.cd :focus-visible { outline: 2px solid var(--ink); outline-offset: 2px; }

.cd-btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; min-height: 40px; padding: 0 16px; border: 1px solid var(--ink); border-radius: 8px; background: var(--ink); color: #fff; font-size: 13px; font-weight: 600; transition: background .15s; }
.cd-btn:hover { background: #262624; }
.cd-btn.yellow { background: var(--y); border-color: var(--y); color: #000; }
.cd-btn.yellow:hover { background: #ffe600; border-color: #ffe600; }
.cd-btn.sm { min-height: 32px; padding: 0 12px; font-size: 12px; }
.cd-btn.ghost { background: #fff; color: var(--ink); border-color: var(--line); }
.cd-btn.ghost:hover { background: var(--soft); }
.cd-link { padding: 0; border: 0; background: none; color: var(--ink); font-size: 13px; font-weight: 600; text-decoration: underline; text-underline-offset: 3px; text-decoration-color: #c9c9c2; }
.cd-link:hover { text-decoration-color: var(--ink); }

.cd-head { display: flex; align-items: flex-end; justify-content: space-between; gap: 20px; margin-bottom: 24px; }
.cd-head h1 { font-size: clamp(24px, 3vw, 30px); font-weight: 700; letter-spacing: -.02em; line-height: 1.2; }
.cd-head p { margin-top: 6px; color: var(--muted); }

/* the one bold element: live job */
.cd-live { padding: 24px; margin-bottom: 20px; border: 1px solid var(--line); border-radius: 14px; background: #fff; color: var(--ink); }
.cd-live-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 20px; }
.cd-live small { display: block; margin-bottom: 6px; color: var(--muted); font-size: 12px; }
.cd-live h2 { font-size: clamp(20px, 2.6vw, 26px); font-weight: 700; letter-spacing: -.02em; line-height: 1.25; }
.cd-live .addr { margin-top: 6px; color: var(--muted); overflow-wrap: anywhere; }
.cd-seg { display: grid; grid-template-columns: repeat(6, 1fr); gap: 4px; margin-top: 24px; }
.cd-seg i { height: 6px; border-radius: 3px; background: #e9e9e4; }
.cd-seg i.on { background: var(--ink); }
.cd-seg i.cur { background: var(--y); box-shadow: 0 0 0 2px var(--ink); }
.cd-seg-labels { display: grid; grid-template-columns: repeat(6, 1fr); gap: 4px; margin-top: 8px; }
.cd-seg-labels span { color: var(--muted); font-size: 11px; text-align: center; }
.cd-seg-labels span.on { color: var(--ink); font-weight: 600; }
.cd-who { display: flex; align-items: center; gap: 12px; margin-top: 20px; padding-top: 18px; border-top: 1px solid var(--line); }
.cd-av { width: 40px; height: 40px; flex: 0 0 40px; border-radius: 50%; object-fit: cover; background: var(--soft); }
.cd-who b { display: block; font-size: 14px; font-weight: 600; }
.cd-who span { color: var(--muted); font-size: 13px; }

/* summary strip */
.cd-strip { display: grid; grid-template-columns: repeat(4, 1fr); margin-bottom: 20px; border: 1px solid var(--line); border-radius: 14px; background: #fff; }
.cd-stat { padding: 18px 22px; min-width: 0; }
.cd-stat + .cd-stat { border-left: 1px solid var(--line); }
.cd-stat span { display: block; color: var(--muted); font-size: 13px; }
.cd-stat b { display: block; margin-top: 4px; font-size: clamp(20px, 2.2vw, 26px); font-weight: 700; letter-spacing: -.02em; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }

.cd-panel { min-width: 0; border: 1px solid var(--line); border-radius: 14px; background: #fff; }
.cd-panel-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 16px 20px; border-bottom: 1px solid var(--line); }
.cd-panel-head h3 { font-size: 15px; font-weight: 600; }
.cd-panel-body { padding: 20px; }

.cd-todo { margin-bottom: 20px; }
.cd-row { display: flex; align-items: center; gap: 14px; padding: 14px 20px; }
.cd-row + .cd-row { border-top: 1px solid var(--line); }
.cd-dot { width: 8px; height: 8px; flex: 0 0 8px; border-radius: 50%; background: var(--y); box-shadow: 0 0 0 3px #fffbc7; }
.cd-row-main { flex: 1; min-width: 0; }
.cd-row-main b { display: block; font-size: 14px; font-weight: 600; }
.cd-row-main span { display: block; overflow: hidden; color: var(--muted); font-size: 13px; text-overflow: ellipsis; white-space: nowrap; }

.cd-grid { display: grid; grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr); gap: 20px; margin-bottom: 20px; align-items: start; }
.cd-act { display: flex; align-items: center; gap: 12px; width: 100%; padding: 13px 20px; border: 0; background: none; color: inherit; text-align: left; }
.cd-act + .cd-act { border-top: 1px solid var(--line); }
.cd-act:hover { background: #fafaf8; }
.cd-badge { flex: 0 0 auto; padding: 3px 9px; border-radius: 6px; background: var(--soft); color: #3a3a36; font-size: 12px; font-weight: 600; white-space: nowrap; }
.cd-badge.good { background: #e3f4e8; color: #17602f; }
.cd-badge.bad { background: #fbe6e4; color: #8f1f19; }
.cd-badge.live { background: #fff7a8; color: #4d4600; }
.cd-chev { color: #a5a59e; }

.cd-total { font-size: clamp(24px, 3vw, 30px); font-weight: 700; letter-spacing: -.02em; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
.cd-cap { margin-top: 2px; color: var(--muted); font-size: 13px; }
.cd-chart { display: flex; align-items: flex-end; gap: 8px; height: 120px; margin-top: 18px; border-bottom: 1px solid var(--line); }
.cd-chart div { flex: 1; min-height: 3px; border-radius: 3px 3px 0 0; background: #dcdcd6; }
.cd-chart div:last-child { background: var(--ink); }
.cd-axis { display: flex; justify-content: space-between; margin-top: 8px; color: var(--muted); font-size: 12px; }

.cd-empty { padding: 36px 20px; text-align: center; }
.cd-empty b { display: block; font-size: 15px; font-weight: 600; }
.cd-empty p { max-width: 320px; margin: 6px auto 16px; color: var(--muted); }

.cd-again { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; padding: 16px; }
.cd-pick { display: flex; align-items: center; gap: 12px; padding: 12px; border: 1px solid var(--line); border-radius: 10px; background: #fff; text-align: left; color: inherit; }
.cd-pick:hover { border-color: var(--ink); }
.cd-pick b { display: block; font-size: 14px; font-weight: 600; overflow-wrap: anywhere; }
.cd-pick span { display: block; color: var(--muted); font-size: 12px; }
.cd-pick em { margin-left: auto; font-style: normal; font-size: 12px; font-weight: 600; white-space: nowrap; }

@media (max-width: 900px) {
  .cd-strip { grid-template-columns: 1fr 1fr; }
  .cd-stat:nth-child(3) { border-left: 0; }
  .cd-stat:nth-child(n+3) { border-top: 1px solid var(--line); }
  .cd-grid { grid-template-columns: 1fr; }
  .cd-again { grid-template-columns: 1fr 1fr; }
}
@media (max-width: 560px) {
  .cd-head { flex-direction: column; align-items: stretch; }
  .cd-live { padding: 18px; }
  .cd-live-top { flex-direction: column; }
  .cd-live-top .cd-btn, .cd-head .cd-btn { width: 100%; }
  .cd-seg-labels span { font-size: 9px; }
  .cd-row { flex-wrap: wrap; padding: 14px 16px; }
  .cd-row-main { flex-basis: calc(100% - 24px); }
  .cd-again { grid-template-columns: 1fr; }
}
`;

export default function ClientDashboardTab({ client = {}, jobs = [], onNavigate, onOpenJob, onHireAgain }) {
  const safeJobs = Array.isArray(jobs) ? jobs.filter(Boolean) : [];

  const d = useMemo(() => {
    const paid = safeJobs.filter(isPaid);
    const completed = safeJobs.filter(isCompleted);
    const active = safeJobs.filter(isTrackable);
    const now = new Date();
    const live = [...active].sort((a, b) => getJobTime(b) - getJobTime(a))[0] || null;

    const paidThisMonth = paid
      .filter((j) => {
        const dt = new Date(j.paid_at || j.updated_at || j.created_at || 0);
        return !Number.isNaN(dt.getTime()) && dt.getMonth() === now.getMonth() && dt.getFullYear() === now.getFullYear();
      })
      .reduce((s, j) => s + paidAmount(j), 0);

    const weeks = Array.from({ length: 8 }, (_, i) =>
      paid
        .filter((j) => Math.floor((Date.now() - getJobTime(j)) / 604800000) === 7 - i)
        .reduce((s, j) => s + paidAmount(j), 0)
    );

    const attention = [];
    safeJobs.forEach((job) => {
      const status = `${statusOf(job)} ${dispatchOf(job)}`;
      const who = craftsmanName(job) || "Your craftsman";
      if (/quote_submitted|quote.*submit/.test(status)) {
        attention.push({ job, title: `${who} sent a quote`, description: serviceOf(job), action: "Review quote" });
      } else if (isCompleted(job) && !isPaid(job) && !isCancelled(job)) {
        attention.push({ job, title: `${who} finished the job`, description: `${serviceOf(job)}. Check the work, then pay.`, action: "Review job" });
      }
    });
    paid
      .filter((j) => !j.rating && Date.now() - getJobTime(j) < 1209600000)
      .slice(0, 2)
      .forEach((job) => attention.push({ job, title: "Rate your craftsman", description: serviceOf(job), action: "Rate" }));

    const seen = new Set();
    const repeat = [];
    completed.forEach((job) => {
      const c = craftsmanOf(job);
      if (c?.id != null && !seen.has(c.id) && repeat.length < 6) {
        seen.add(c.id);
        repeat.push({ craftsman: c, service: serviceOf(job) });
      }
    });

    return {
      live, attention, repeat, weeks, paidThisMonth,
      totalSpent: paid.reduce((s, j) => s + paidAmount(j), 0),
      activeCount: active.length,
      completedCount: completed.length,
      recent: [...safeJobs].sort((a, b) => getJobTime(b) - getJobTime(a)).slice(0, 6),
    };
  }, [safeJobs]);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const firstName = (client?.full_name || client?.name || "there").trim().split(/\s+/)[0] || "there";
  const step = d.live ? getStep(d.live) : 0;
  const maxWeek = Math.max(...d.weeks, 1);

  const openJob = (job) => (onOpenJob ? onOpenJob(job) : onNavigate?.("track"));

  const label = (job) => {
    if (/quote_submitted/.test(statusOf(job))) return "Quote received";
    if (isPaid(job)) return "Paid";
    if (isCancelled(job)) return "Cancelled";
    if (isCompleted(job)) return "Completed";
    return String(job?.dispatch_status || job?.status || "Pending")
      .replace(/[_-]/g, " ")
      .replace(/^\w/, (c) => c.toUpperCase());
  };
  const badgeCls = (job) =>
    isPaid(job) || isCompleted(job) ? "good" : isCancelled(job) ? "bad" : "live";

  return (
    <div className="cd">
      <style>{CSS}</style>

      <header className="cd-head">
        <div>
          <h1>{greeting}, {firstName}</h1>
          <p>
            {d.activeCount > 0
              ? `You have ${d.activeCount} active ${d.activeCount === 1 ? "request" : "requests"}.`
              : "Nothing in progress right now."}
          </p>
        </div>
        <button type="button" className="cd-btn yellow" onClick={() => onNavigate?.("request")}>
          <i className="fas fa-bolt" aria-hidden="true" /> Request a service
        </button>
      </header>

      {d.live && (
        <section className="cd-live" aria-label="Current service request">
          <div className="cd-live-top">
            <div>
              <small>Current request</small>
              <h2>{HEADLINES[step]}</h2>
              <p className="addr">{d.live.address || d.live.location || "No address provided"}</p>
            </div>
            <button type="button" className="cd-btn yellow" onClick={() => openJob(d.live)}>Track job</button>
          </div>

          <div className="cd-seg" role="progressbar" aria-valuemin={0} aria-valuemax={5} aria-valuenow={step} aria-label={`Job progress: ${STEPS[step]}`}>
            {STEPS.map((s, i) => <i key={s} className={i < step ? "on" : i === step ? "cur" : ""} />)}
          </div>
          <div className="cd-seg-labels">
            {STEPS.map((s, i) => <span key={s} className={i === step ? "on" : ""}>{s}</span>)}
          </div>

          {craftsmanName(d.live) && (
            <div className="cd-who">
              <img className="cd-av" src={avatar(craftsmanName(d.live))} alt="" />
              <div>
                <b>{craftsmanName(d.live)}</b>
                <span>{d.live.eta_minutes && step === 2 ? `Arriving in about ${d.live.eta_minutes} minutes` : "Assigned to your job"}</span>
              </div>
            </div>
          )}
        </section>
      )}

      <section className="cd-strip" aria-label="Account summary">
        {[
          ["Active requests", d.activeCount],
          ["Completed jobs", d.completedCount],
          ["Total paid", money(d.totalSpent)],
          ["Paid this month", money(d.paidThisMonth)],
        ].map(([l, v]) => (
          <div className="cd-stat" key={l}><span>{l}</span><b>{v}</b></div>
        ))}
      </section>

      {d.attention.length > 0 && (
        <section className="cd-panel cd-todo">
          <div className="cd-panel-head">
            <h3>Needs your attention</h3>
            <span className="cd-badge live">{d.attention.length}</span>
          </div>
          {d.attention.map(({ job, title, description, action }, i) => (
            <div className="cd-row" key={`${jobKey(job)}-a-${i}`}>
              <span className="cd-dot" aria-hidden="true" />
              <div className="cd-row-main"><b>{title}</b><span>{description}</span></div>
              <button type="button" className="cd-btn sm" onClick={() => openJob(job)}>{action}</button>
            </div>
          ))}
        </section>
      )}

      <div className="cd-grid">
        <section className="cd-panel">
          <div className="cd-panel-head">
            <h3>Recent requests</h3>
            <button type="button" className="cd-link" onClick={() => onNavigate?.("track")}>View all</button>
          </div>
          {d.recent.length === 0 ? (
            <div className="cd-empty">
              <b>No requests yet</b>
              <p>Book a service and its progress will show up here.</p>
              <button type="button" className="cd-btn" onClick={() => onNavigate?.("request")}>Request a service</button>
            </div>
          ) : (
            d.recent.map((job, i) => (
              <button type="button" className="cd-act" key={jobKey(job, i)} onClick={() => openJob(job)} aria-label={`Open ${serviceOf(job)}`}>
                <div className="cd-row-main">
                  <b>{serviceOf(job)}</b>
                  <span>
                    {craftsmanName(job) || "Waiting for a craftsman"}
                    {job?.budget != null && job.budget !== "" ? `, ${money(job.budget)}` : ""}
                  </span>
                </div>
                <span className={`cd-badge ${badgeCls(job)}`}>{label(job)}</span>
                <i className="fas fa-chevron-right cd-chev" aria-hidden="true" />
              </button>
            ))
          )}
        </section>

        <section className="cd-panel">
          <div className="cd-panel-head"><h3>Spending</h3></div>
          <div className="cd-panel-body">
            <p className="cd-total">{money(d.totalSpent)}</p>
            <p className="cd-cap">Paid so far, last 8 weeks below</p>
            <div className="cd-chart" role="img" aria-label="Payments over the last eight weeks">
              {d.weeks.map((a, i) => (
                <div key={i} title={money(a)} style={{ height: `${Math.max((a / maxWeek) * 100, 3)}%` }} />
              ))}
            </div>
            <div className="cd-axis"><span>8 weeks ago</span><span>This week</span></div>
          </div>
        </section>
      </div>

      {d.repeat.length > 0 && (
        <section className="cd-panel">
          <div className="cd-panel-head"><h3>Book a craftsman again</h3></div>
          <div className="cd-again">
            {d.repeat.map(({ craftsman, service }) => {
              const name = craftsman.full_name || craftsman.name || "Craftsman";
              return (
                <button
                  type="button"
                  className="cd-pick"
                  key={craftsman.id}
                  onClick={() =>
                    onHireAgain
                      ? onHireAgain({ ...craftsman, primary_service: craftsman.primary_service || service })
                      : onNavigate?.("request")
                  }
                >
                  <img className="cd-av" src={avatar(name)} alt="" />
                  <div><b>{name}</b><span>{craftsman.primary_service || service}</span></div>
                  <em>Book</em>
                </button>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
